import { useEffect, useMemo, useState } from "react"
import { useSearch } from "@tanstack/react-router"
import {
	IconCheckOutline18 as Check,
	IconGlobeOutline18 as Globe,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { Badge, Button, Dialog } from "@productlane/ui"

import { useZeroWorkspace } from "@/lib/zero"

export const MAIN_PORTAL_NAME = "Main portal"

/** The query key an import page reads its target portal back from. */
export const PORTAL_PARAM = "portal"

export interface ImportPortal {
	id: string | null
	name: string
}

/** Every portal an import can go to, with the main one first. */
export function useImportPortals(): {
	portals: ImportPortal[]
	hasChoice: boolean
} {
	const workspace = useZeroWorkspace()
	const instances = workspace?.portalInstances

	return useMemo(() => {
		const named: readonly { id: string; name: string }[] = instances ?? []
		const portals: ImportPortal[] = [
			{ id: null, name: MAIN_PORTAL_NAME },
			...named.map((portal) => ({
				id: portal.id as string | null,
				name: portal.name,
			})),
		]
		return { portals, hasChoice: named.length > 0 }
	}, [instances])
}

/** Adds the chosen portal to an import route, leaving the main portal bare. */
export function importPath(path: string, portalInstanceId: string | null) {
	if (!portalInstanceId) return path
	return `${path}?${PORTAL_PARAM}=${encodeURIComponent(portalInstanceId)}`
}

/** The portal an import page was opened for. Null is the main portal. */
export function useImportPortalId(): string | null {
	return useSearch({ strict: false, select: (s) => s[PORTAL_PARAM] }) ?? null
}

/** The name of that portal, for a page that tells the user where data goes. */
export function useImportPortalName(): string | null {
	const portalInstanceId = useImportPortalId()
	const { portals, hasChoice } = useImportPortals()
	if (!hasChoice) return null
	return portals.find((portal) => portal.id === portalInstanceId)?.name ?? null
}

/** Names the portal an import page writes to, so the choice stays visible. */
export function ImportPortalBadge() {
	const name = useImportPortalName()
	if (!name) return null

	return <Badge Icon={Globe}>{name}</Badge>
}

interface Props {
	isOpen: boolean
	/** What is being imported, as it reads in "Which portal gets these X?". */
	subject: string
	portals: ImportPortal[]
	onOpenChange: (value: boolean) => void
	onConfirm: (portalInstanceId: string | null) => void
}

/** Asks which portal an import writes to, for a workspace that runs several. */
export function ImportPortalDialog({
	isOpen,
	subject,
	portals,
	onOpenChange,
	onConfirm,
}: Props) {
	const [selected, setSelected] = useState<string | null>(null)

	// Radix locks the body while a modal is open and lifts it on its own close
	// sequence, which an unmount skips. The lock then outlives the dialog and
	// every click on the page behind it dies.
	useEffect(() => {
		if (!isOpen) return
		return () => {
			document.body.style.pointerEvents = ""
		}
	}, [isOpen])

	return (
		<Dialog
			className="max-w-96"
			isOpen={isOpen}
			onCloseAutoFocus={(event) => {
				event.preventDefault()
				document.body.style.pointerEvents = ""
			}}
			onOpenChange={(value) => {
				if (!value) setSelected(null)
				onOpenChange(value)
			}}
			title="Pick a portal"
			description={`Which portal gets these ${subject}? You can move them later.`}
			footer={
				<Button
					onClick={() => {
						onConfirm(selected)
						setSelected(null)
					}}
				>
					Continue
				</Button>
			}
		>
			<div className="mt-3 flex flex-col gap-0.5">
				{portals.map((portal) => {
					const isSelected = portal.id === selected
					return (
						<button
							key={portal.id ?? "__main__"}
							type="button"
							onClick={() => setSelected(portal.id)}
							className={cn(
								"flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm",
								"hover:bg-gray-100 dark:hover:bg-gray-800",
								isSelected && "bg-gray-100 dark:bg-gray-800",
							)}
						>
							<span className="flex min-w-0 items-center gap-2">
								<Globe className="h-3.5 w-3.5 flex-none text-tertiary" />
								<span className="truncate" title={portal.name}>
									{portal.name}
								</span>
							</span>
							{isSelected && <Check className="h-4 w-4 flex-none" />}
						</button>
					)
				})}
			</div>
		</Dialog>
	)
}
